import React, { FunctionComponent } from 'react';
import { LayoutWrapper, Layout } from 'components/Layout/Layout';
import Head from 'next/head';
import styled from 'styled-components';
import { Header as HeaderComponent } from 'components/Layout/Header';
import { Footer } from 'components/Layout/Footer';
import { Hero } from '../../components/2025/Hero';
import { Competition } from '../../components/2025/Competition';
import { Submissions } from '../../components/2025/Submissions';
import { Support } from '../../components/2025/Support';
import { Recognition } from '../../components/2025/Recognition';
import { Impact } from '../../components/2025/Impact';
import Link from 'next/link';

const slides = [
  {
    title: 'About the Earth Prize',
    picture: '/images/2025-students.webp',
    mobilePosition: 'center 30%',
    subtitle:
      'The Earth Prize is the world’s largest environmental sustainability competition for students aged 13-19, offering $100,000 in prizes for the most impactful projects addressing critical environmental issues.',
    text: 'The competition empowers young innovators to tackle global challenges through creative and actionable solutions that can make a difference.',
  },
  {
    title: 'Who’s behind The Earth Prize?',
    picture: '/images/2025-students2.webp',
    mobilePosition: 'center',
    subtitle:
      'The Earth Prize is the first and flagship initiative of The Earth Foundation, a non-profit organization based in Geneva, Switzerland.',
    text: 'The Foundation is dedicated to inspiring, educating, mentoring, and empowering students and young entrepreneurs to address environmental challenges with innovative ideas and practical solutions.',
  },
  {
    title: 'Why was The Earth Prize created?',
    picture: '/images/system-change.webp',
    mobilePosition: 'center',
    subtitle:
      'The Earth Prize was born out of the global climate movement of 2019, when thousands of students rallied for climate action. This inspiring moment led to the founding of The Earth Foundation, a Swiss and U.S. non-profit organization.',
    text: 'Since then, The Earth Prize has grown into the largest environmental competition for young people, with participants from over 150 countries joining its first three editions.',
  },
];

const criterias = [
  {
    title: 'Eligibility',
    text: 'The Earth Prize is open to students worldwide who meet the following criteria:',
  },
  {
    title: 'Age',
    text: 'Participants must have been born between 2005 and 2011 (inclusive).',
  },
  {
    title: 'Education',
    text: 'The competition is for secondary education students only (not tertiary education).',
  },
  {
    title: 'Institution',
    text: 'Students must be enrolled in a school or educational program. Homeschooled students are also eligible. There are no geographic restrictions—students from any country can participate.',
  },
];

const submissionsTabs = [
  {
    name: 'Projects',
    bgColor: '#21B7BB1A',
    textColor: '#2E5B66',
    text: (
      <>
        The Earth Prize seeks projects that tackle environmental challenges and
        inspire positive change. Participants can submit either groundbreaking
        new ideas or showcase existing projects. Whether local, national, or
        global, all projects are welcome—whether they take the form of
        organizations, enterprises, or campaigns.
      </>
    ),
  },
  {
    name: 'Areas of focus',
    bgColor: '#9F605E33',
    textColor: '#9F605E',
    text: (
      <>
        Projects should broadly focus on environmental sustainability issues. A
        variety of approaches are encouraged, and participants are free to
        explore solutions to different environmental challenges.
      </>
    ),
  },
  {
    name: 'Evaluations',
    bgColor: '#ED9F6E33',
    textColor: '#ED9F6E',
    text: (
      <>
        Projects will be evaluated based on their potential to solve
        environmental challenges, their innovation, feasibility, and the impact
        they can have locally or globally. For a detailed breakdown of how your
        project will be assessed,{' '}
        <Link
          className="text-[#ED9F6E] hover:underline"
          href="/competition#evaluation-criteria"
        >
          click here
        </Link>
        .
      </>
    ),
  },
  {
    name: 'Deadline',
    bgColor: '#00B7BD33',
    textColor: '#00B7BD',
    text: (
      <>
        All projects must be submitted by <b>January 31st, 2025.</b>
      </>
    ),
  },
];

const supportTabs = [
  {
    name: 'Learning Content',
    link: '/learning-content',
    image: '/images/LearningContent2025.webp',
    images: [
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/0a3f8a07fa/interconnecteness.png',
        position: '30% center',
      },
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/d84363fb39/the-anthropocene.png',
        position: 'center',
      },
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/38351623b3/sustainability.png',
        position: '30% center',
      },
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/b84161cf73/transition.png',
        position: 'center',
      },
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/98f4fc893a/benjamin-stern.png',
        position: '30% center',
      },
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/6cb9473c7b/sam-teicher.png',
        position: '30% center',
      },
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/580b208a63/louise-mabulo.png',
        position: 'center',
      },
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/ca54e21cb2/boyan-slat.png',
        position: '30% center',
      },
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/72d358efa8/tom-szaky.png',
        position: '10% center',
      },
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/0e1ee3f37a/omar-itani.png',
        position: '60% center',
      },
      {
        image:
          'https://a.storyblok.com/f/160365/1920x807/c3eca71c7a/lesein-mutunkei.png',
        position: 'center',
      },
    ],
    text: 'Participants will gain access to exclusive learning materials, from in-depth content about environmental sustainability to inspiring stories of young environmental changemakers. They will also get practical tips and advice on how to become changemakers themselves.',
  },
  {
    name: 'Mentors',
    image: '/images/Mentors2025.webp',
    text: 'Our Mentors are passionate university students from around the globe, studying sustainability and entrepreneurship. This year, The Earth Prize has selected 50 university students to guide and support participants through The Mentorship Portal or via one-on-one sessions. Whether students need advice or a sounding board for their ideas, our mentors are here to help them every step of the way.',
    images: [
      {
        image: '/images/stephen.webp',
        position: 'center',
      },
      {
        image: '/images/chloe.webp',
        position: 'center',
      },
      {
        image: '/images/pierfrancesco.webp',
        position: 'center',
      },
      {
        image: '/images/elham.webp',
        position: 'center',
      },
      {
        image: '/images/francisca.webp',
        position: 'center',
      },
      {
        image: '/images/yoong.webp',
        position: 'center',
      },
      {
        image: '/images/annamaria.webp',
        position: 'center',
      },
    ],
  },
  {
    name: 'Ambassadors',
    link: '/ambassadors',
    image: '/images/jonathan.jpeg',
    images: [
      {
        image: '/images/suzy.jpeg',
        position: 'center',
      },
      {
        image: '/images/jonathan.jpeg',
        position: 'center',
      },
      {
        image: '/images/beatrice.jpeg',
        position: 'center',
      },
      {
        image: '/images/kasha.webp',
        position: 'center',
      },
      {
        image: '/images/navya.jpeg',
        position: 'center',
      },
      {
        image: '/images/rao.jpeg',
        position: 'center',
      },
      {
        image: '/images/okezue.jpeg',
        position: 'center',
      },
      {
        image: '/images/louie.jpeg',
        position: 'center',
      },
      {
        image: '/images/natalie.jpg',
        position: 'center',
      },
    ],
    text: 'Inspiring role models from the sustainability field will be there to provide advice and guidance to teams. Our Ambassadors are individuals dedicated to helping young changemakers make a real impact.',
  },
];

const EarthPrize2025: FunctionComponent = () => {
  return (
    <Layout>
      <Head>
        <title>The Earth Prize - 2025</title>
        <meta
          name="description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:title" content="The Earth Prize - 2025" />
        <meta
          property="og:description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:image" content="/images/cover.jpg" />
      </Head>
      <HeaderComponent />
      <LayoutWrapper>
        <VolunteerWrapper>
          <Hero slides={slides} />
          <Competition
            text1="The competition"
            text2="Registration Form"
            text3=""
            text4="Registration closed on November 30th."
            text5="Teams"
            text6="students per team"
            text7="Global Winner Announcement"
            text8="Earth Day 2025, April 22nd"
            text9={
              <>
                Detailed
                <br />
                timeline
              </>
            }
            text10="Check here"
            text11="Total prize money"
            text12={
              <>
                <b>Educator of the year</b>
                <br />
                Two educators receive <b>$2,500</b> each
              </>
            }
            text13={
              <>
                <b>Mentor of the year</b>
                <br />
                Three mentors receive <b>$2,500</b> each
              </>
            }
            text14={
              <>
                <b>Regional Winners</b>
                <br />
                Seven teams (one from each region) receive <b>$12,500</b> each
                to develop and implement their idea.
              </>
            }
            text15="The regions: Oceania & Southeast Asia, Asia, Middle East, Africa, Europe, Central & South America, North America"
            criterias={criterias}
          />
          <Submissions
            text1="The submissions"
            submissionsTabs={submissionsTabs}
          />
          <Support text1="Support along the way" supportTabs={supportTabs} />
          <Recognition
            text1="Recognition"
            text2="Regional Winners"
            text3="The top team in each region will be awarded the Regional Winner distinction, based on their final project proposals evaluated by The Earth Prize Adjudicating Panel. These teams will be recognized for their contributions to environmental sustainability."
            text4="Scholars"
            text5="The Scholars distinction is awarded to students whose project proposals demonstrate exceptional quality. Scholars are announced in February, selected by The Earth Prize team and external reviewers."
            text6="Global Winner"
            text7="The Earth Prize Global Winner will be chosen by a public vote from among the seven Regional Winners."
            text8="Educators of the Year"
            text9="Two teachers or educators who have made outstanding contributions to education and mentorship during The Earth Prize competition will be awarded the Educator of the Year award, as selected by The Earth Foundation’s Advisor, Mario Salomone."
            text10="Mentors of the Year"
            text11="The three most engaged Mentors, as voted by the participants, will receive the Mentor of the Year award in recognition of their dedication and support throughout the competition."
          />
          <Impact
            text1={
              <>
                <span className="block mb-[20px]">Impact</span>
                Celebrating
                <br />
                the Legacy of
                <br />
                The Earth Prize
              </>
            }
          />
        </VolunteerWrapper>
      </LayoutWrapper>
      <Footer />
    </Layout>
  );
};

const VolunteerWrapper = styled.div`
  background: #f0f0f0;
`;

export default EarthPrize2025;
